import { Alert, Box, FormControlLabel, Grid, Stack, Switch, Typography } from '@mui/material';
import CollapsibleInfoAlert from '@/components/common/collapsible-info-alert';
import { StageConfigurationWidget } from '@/components/flows/detail/flow-detail-tabs/configuration/stage-configuration-widget';
import { RoomConfigurationWidget } from '@/components/flows/detail/flow-detail-tabs/configuration/room-configuration-widget';
import { InputsConfigurationWidget } from '@/components/flows/detail/flow-detail-tabs/configuration/inputs-configuration-widget';
import { BroadcastConfigurationWidget } from '@/components/flows/detail/flow-detail-tabs/configuration/broadcast-configuration-widget';
import { useConfigurationPlayers } from '@/components/flows/detail/flow-detail-tabs/configuration/use-configuration-players';
import { useScreenSize } from '@/hooks/use-screen-size';
import { useNotifications } from '@/hooks/use-notifications';
import { useTranslations } from '@/i18n/use-translations.tsx';
import { useGetSpacePlayerSetupQuery } from '@/state/queries/players/use-get-space-player-setup';
import { usePatchSpacePlayerSetupMutation } from '@/state/mutations/players/use-patch-space-player-setup';

const PLAYER_PAIRING_URL = import.meta.env.VITE_PLAYER_APP_URL ?? 'https://player.britestage.com';

export function SpaceConfigurationTab({ spaceId, eventId }: { spaceId: string; eventId: string }) {
  const t = useTranslations();
  const notification = useNotifications();
  const { isWideScreen } = useScreenSize({ breakpoint: 860 });
  const { playersByType, pairEntityType } = useConfigurationPlayers({
    parentId: spaceId,
    parentType: 'Space',
  });
  const { data: spaceSetup } = useGetSpacePlayerSetupQuery({ spaceId, eventId });
  const { mutateAsync: patchSpaceSetup, isPending } = usePatchSpacePlayerSetupMutation();

  const stagePlayers = playersByType.get('stagePlayer') ?? [];
  const confidencePlayers = playersByType.get('confidencePlayer') ?? [];
  const roomPlayers = playersByType.get('roomPlayer') ?? [];
  const mediaPlayers = playersByType.get('screenPublisher') ?? [];
  const onlinePlayers = playersByType.get('onlinePlayer') ?? [];
  const liveLocked = spaceSetup?.liveLocked === true;

  const pairingInfoAlert = (
    <CollapsibleInfoAlert
      title={t('flows.detail.configuration.pairingInfo.title')}
      description={t('flows.detail.configuration.pairingInfo.body', {
        playerUrl: PLAYER_PAIRING_URL,
      })}
      sx={{ mt: 0 }}
    />
  );

  const leftColumn = (
    <Stack spacing={2}>
      {pairingInfoAlert}
      <StageConfigurationWidget
        stagePlayers={stagePlayers}
        confidencePlayers={confidencePlayers}
        entityId={spaceId}
        entityType={pairEntityType}
        liveContext={null}
        videoPlayerId={spaceSetup?.videoPlayerId}
        audioPlayerId={spaceSetup?.audioPlayerId}
      />
      <RoomConfigurationWidget
        roomPlayers={roomPlayers}
        entityId={spaceId}
        entityType={pairEntityType}
        liveContext={null}
        videoPlayerId={spaceSetup?.videoPlayerId}
        audioPlayerId={spaceSetup?.audioPlayerId}
      />
    </Stack>
  );

  const rightColumn = (
    <Stack spacing={2}>
      <InputsConfigurationWidget
        mediaPlayers={mediaPlayers}
        entityId={spaceId}
        entityType={pairEntityType}
        liveContext={null}
        avSources={spaceSetup}
        avSelectDisabled={liveLocked || isPending}
        onChangeAvSources={async (next) => {
          try {
            await patchSpaceSetup({
              spaceId,
              eventId,
              videoPlayerId: next.videoPlayerId,
              audioPlayerId: next.audioPlayerId,
            });
          } catch {
            notification.notifyError(t('event.detail.space.saveError'));
          }
        }}
        videoPlayerId={spaceSetup?.videoPlayerId}
        audioPlayerId={spaceSetup?.audioPlayerId}
      />
      <BroadcastConfigurationWidget
        onlinePlayers={onlinePlayers}
        entityId={spaceId}
        entityType={pairEntityType}
        liveContext={null}
        videoPlayerId={spaceSetup?.videoPlayerId}
        audioPlayerId={spaceSetup?.audioPlayerId}
      />
    </Stack>
  );

  return (
    <Box
      width="100%"
      sx={{
        pb: {
          xs: 'calc(96px + env(safe-area-inset-bottom, 0px))',
          md: 3,
        },
      }}
    >
      <Stack spacing={2} sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={spaceSetup?.playerSetupEnabled === true}
              disabled={liveLocked || isPending || !spaceSetup}
              onChange={async (event) => {
                try {
                  await patchSpaceSetup({
                    spaceId,
                    eventId,
                    playerSetupEnabled: event.target.checked,
                  });
                } catch {
                  notification.notifyError(t('event.detail.space.toggleError'));
                }
              }}
            />
          }
          label={t('event.detail.space.usePlayerSetup')}
        />
        <Typography variant="body2" color="text.secondary">
          {t('event.detail.space.usePlayerSetupHelp')}
        </Typography>
        {liveLocked ? <Alert severity="info">{t('flows.detail.configuration.inputs.liveLocked')}</Alert> : null}
      </Stack>

      {isWideScreen ? (
        <Grid container spacing={2}>
          <Grid size={6}>{leftColumn}</Grid>
          <Grid size={6}>{rightColumn}</Grid>
        </Grid>
      ) : (
        <Stack spacing={2}>
          {leftColumn}
          {rightColumn}
        </Stack>
      )}
    </Box>
  );
}
